import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatCardModule } from "@angular/material/card";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { PageEvent } from "@angular/material/paginator";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatSortModule, Sort } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { concatMap, finalize, from, Observable, of, Subject, switchMap, take, toArray } from "rxjs";
import { ClientService } from "../../services/client.service";
import { NotificationService } from "../../services/notification.service";
import { LoadingComponent } from "../../shared/loading/loading.component";
import { SearchBarComponent } from "../../shared/searchbar/searchbar.component";
import { SharedTableComponent } from "../../shared/shared-table/shared-table.component";
import { OneboxModalComponent } from "../specific-modals/onebox-modal/onebox-modal.component";

@Component({
  selector: "app-onebox",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatSortModule,
    MatCardModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    SharedTableComponent,
    SearchBarComponent,
    MatProgressSpinnerModule,
    LoadingComponent
],
  templateUrl: "./onebox.component.html",
  styleUrl: "./onebox.component.scss",
})
export class OneboxComponent implements OnInit, OnDestroy {

  isLoading = false;
  hasError = false;
  isLoadingFullScreen = false;

  oneboxListLoaded$ = new Subject<void>();

  // Stato query
  sortBy = "name:asc";
  sortField = "";
  sortDirection = "";
  searchQuery = "";

  // Paginazione server-side
  pageIndex = 0;
  pageSize = 10;
  totalItems = 0;

  // DataSource
  dataSourceOneBox = new MatTableDataSource<any>();

  // Select alias
  selectAliasOptions = [{ value: '', label: '' }];
  dataKeywordList: any[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly clientService: ClientService,
    private readonly dialog: MatDialog,
    private readonly notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadOneBoxList(this.pageIndex, this.pageSize, this.sortBy, "", "");

  }

  loadOneBoxList(
    pageIndex: number,
    pageSize: number,
    sortBy: string,
    clientId: string,
    oneBoxFilter: string
  ) {
    this.isLoading = true;
    this.hasError = false;
    this.clientService
      .getOneBoxList(pageIndex, pageSize, sortBy, clientId, oneBoxFilter).pipe(finalize(() => { 
        this.isLoading = false;
        this.oneboxListLoaded$.next();
       }))
      .subscribe({
        next: (response) => {
          const content = response?.content ?? [];
          this.dataSourceOneBox.data = content;
          this.totalItems = response?.totalElements ?? 0;

          // allinea lo stato locale con la risposta BE
          this.pageIndex = (response?.number ?? pageIndex) ?? 0;
          this.pageSize  = (response?.size ?? pageSize) ?? this.pageSize;
        },
        error: (err) => {
          console.log("errore nel recupero dei dati", err);
          this.dataSourceOneBox.data = [];
          this.totalItems = 0;
          this.hasError = true;
        },
      });
  }

  onFilterChanged(value: any) {
    this.searchQuery = value?.nome ?? '';
    this.pageIndex = 0;
    this.loadOneBoxList(this.pageIndex, this.pageSize, 'name:asc', '', this.searchQuery);
  }
  

  // Ordinamento
  onSortChanged(sort: Sort) {
    this.sortField = sort.active;
    this.sortDirection = sort.direction || "asc";
    this.sortBy = `${this.sortField}:${this.sortDirection}`;
    this.pageIndex = 0;
    this.loadOneBoxList(this.pageIndex, this.pageSize, this.sortBy, "", this.searchQuery);
  }

  // Cambio pagina / page size (server-side)
  onPageChanged(ev: PageEvent) {
    this.pageIndex = ev.pageIndex;
    this.pageSize = ev.pageSize;
    this.loadOneBoxList(this.pageIndex, this.pageSize, this.sortBy, "", this.searchQuery);
  }

  diffArraysKeywords(original: any[], updated: any[]) {
    const removed: any[] = [];
    const modified: any[] = [];
    const added: any[] = [];

    const originalMap = new Map(original.map((k) => [k.idKeyword, k]));
    const updatedMap = new Map(updated.map(k => [k.idKeyword, k]));

    // aggiunti / modificati
    updated.forEach(updItem => {
      const origItem = originalMap.get(updItem.idKeyword);
      if (!origItem) {
        added.push(updItem);
      } else if (origItem.keyword !== updItem.keyword) {
        modified.push(updItem);
      }
    });

    // rimossi
    original.forEach(origItem => {
      if (!updatedMap.has(origItem.idKeyword)) {
        removed.push(origItem);
      }
    });

    return { removed, modified, added };
  }

  openDialog(event: { data: any; type: 'add' | 'edit' | 'delete' }) {
    const dialogRef = this.dialog.open(OneboxModalComponent, {
      width: "500px",
      data: {
        action: event.type,
        payload: event.data
      },
    });

    // preload keywords se in edit/delete con trigger KEYWORDS
    if (event.type === 'edit' || event.type === 'delete') {
      if (event.data.triggerType === 'KEYWORDS') {
        const oneBoxId = event.data.id;
        dialogRef.componentInstance.isLoading = true;

        this.clientService.getKeywordList(0, 10, "keyword:asc", oneBoxId, '').subscribe({
          next: (response) => {
            dialogRef.componentInstance.setExtraInputs(response.content);
            this.dataKeywordList = response.content;
          },
          error: (err) => {
            console.log("errore nel recupero dei dati", err);
            dialogRef.componentInstance.isLoading = false;
          },
        });

      } else {
        this.dataKeywordList = [];
      }
    }

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.isLoadingFullScreen = true;
      const { keyword, extraInputKeywordArray, ...oneboxPayload } = result;
      
      if (event.type === "add") {
        
        this.clientService.addOnebox(oneboxPayload).pipe(
          switchMap((res: any) => {
            const oneBoxId = res.id;
            const keywords = result.extraInputKeywordArray ?? [];
            if (!keywords.length) return of([]);
            return from(keywords).pipe(
              concatMap((kw: any) => this.clientService.addKeyword(oneBoxId, kw.keyword)),
              toArray(),
              switchMap(() => this.clientService.getKeywordList(0, 10, "keyword:asc", oneBoxId, ""))
            );
          }),
          finalize(()=> {
            this.isLoadingFullScreen = false;
          })
        ).subscribe({
          next: () => {
            // resta sulla pagina corrente
            this.loadOneBoxList(this.pageIndex, this.pageSize, this.sortBy, "", this.searchQuery);
            this.oneboxListLoaded$.pipe(take(1)).subscribe(() => {
              this.notificationService.entitySuccess('add', 'Onebox');
            })
          },
          error: (err) => {
            console.log("errore nella richiesta", err);
            this.notificationService.entityError('add', 'Onebox');
          }
        });

      } else if (event.type === "edit") {
        const index = this.dataSourceOneBox.data.findIndex((e: any) => e.id === result.id);
        if (index !== -1) {
          const oneBoxId = result.id;

          if (this.dataKeywordList.length) {
            // triggerType cambiato: rimuovi tutte le keywords
            if (result.triggerType !== 'KEYWORDS') {
              this.clientService.editOnebox(oneboxPayload).pipe(
                switchMap(() =>
                  from(this.dataKeywordList).pipe(
                    concatMap((kw: any) => this.clientService.deleteKeyword(kw.idKeyword)),
                    toArray(),
                    switchMap(() => this.clientService.getKeywordList(0, 10, 'keyword:asc', oneBoxId, ''))
                  )
                ),
                finalize(()=> {
                  this.isLoadingFullScreen = false;
                })
              ).subscribe({
                next: () => {
                  this.loadOneBoxList(this.pageIndex, this.pageSize, 'name:asc', '', '');
                  this.oneboxListLoaded$.pipe(take(1)).subscribe(()=>{
                    this.notificationService.entitySuccess('update', 'Onebox');
                  })
                },
                error: (err) => {
                  console.log("errore nella richiesta", err);
                  this.notificationService.entityError('update', 'Onebox');
                }
              });

            } else {
              // triggerType invariato: calcola differenze
              const diffArrays = this.diffArraysKeywords(this.dataKeywordList, result.extraInputKeywordArray ?? []);
              this.clientService.editOnebox(oneboxPayload).pipe(
                switchMap(() => {
                  const ops: Observable<any>[] = [];
                  if (diffArrays.added.length) {
                    ops.push(...diffArrays.added.map((kw: any) => this.clientService.addKeyword(oneBoxId, kw.keyword)));
                  }
                  if (diffArrays.removed.length) {
                    ops.push(...diffArrays.removed.map((kw: any) => this.clientService.deleteKeyword(kw.idKeyword)));
                  }
                  if (diffArrays.modified.length) {
                    ops.push(...diffArrays.modified.map((kw: any) => this.clientService.editKeyword(kw)));
                  }
                  if (!ops.length) return of([]);
                  return from(ops).pipe(concatMap(op => op), toArray());
                }),
                switchMap(() => this.clientService.getKeywordList(0, 10, 'keyword:asc', oneBoxId, '')),
                finalize(()=> {this.isLoadingFullScreen = false;})
              ).subscribe({
                next: () => {
                  this.loadOneBoxList(this.pageIndex, this.pageSize, 'name:asc', '', '');
                  this.oneboxListLoaded$.pipe(take(1)).subscribe(() => {
                    this.notificationService.entitySuccess('update', 'Onebox');
                  })
                },
                error: (err) => {
                  console.log("errore nella richiesta", err);
                  this.notificationService.entityError('update', 'Onebox');
                }
              });
            }

          } else {
            // inizialmente senza keywords
            this.clientService.editOnebox(oneboxPayload).pipe(
              switchMap(() => {
                if (result.triggerType === 'KEYWORDS') {
                  const keywords = result.extraInputKeywordArray ?? [];
                  if (keywords.length) {
                    return from(keywords).pipe(
                      concatMap((kw: any) => this.clientService.addKeyword(oneBoxId, kw.keyword)),
                      toArray(),
                      switchMap(() => this.clientService.getKeywordList(0, 10, 'keyword:asc', oneBoxId, ''))
                    );
                  }
                }
                return this.clientService.getKeywordList(0, 10, 'keyword:asc', oneBoxId, '');
              }),
              finalize(()=> { this.isLoadingFullScreen = false; })
            ).subscribe({
              next: () => {
                this.loadOneBoxList(this.pageIndex, this.pageSize, 'name:asc', '', '');
                this.oneboxListLoaded$.pipe(take(1)).subscribe(()=> {
                  this.notificationService.entitySuccess('update', 'Onebox');
                })
              },
              error: (err) => {
                console.log("errore nella richiesta", err);
                this.notificationService.entityError('update', 'Onebox');
              }
            });
          }
        }

      } else if (event.type === "delete") {
        const keywords = this.dataKeywordList ?? [];
        let deleteKeywords$: Observable<any>;

        if (keywords.length) {
          deleteKeywords$ = from(keywords).pipe(
            concatMap((kw: any) => this.clientService.deleteKeyword(kw.idKeyword)),
            toArray()
          );
        } else {
          deleteKeywords$ = of([]);
        }

        deleteKeywords$.pipe(
          switchMap(() => this.clientService.deleteOnebox(result.id)),
          finalize(()=> { this.isLoadingFullScreen = false; })
        ).subscribe({
          next: () => {
            // se sto cancellando l’unico item della pagina corrente, scala pagina
            if (this.pageIndex > 0 && this.dataSourceOneBox.data.length === 1) {
              this.pageIndex = this.pageIndex - 1;
            }
            this.loadOneBoxList(this.pageIndex, this.pageSize, 'name:asc', '', '');
            this.oneboxListLoaded$.pipe(take(1)).subscribe(()=> {
              this.notificationService.entitySuccess('delete', 'Onebox');
            })
          },
          error: (err) => {
            if(err.status !== 409) {
              console.log("errore nella richiesta", err);
              this.notificationService.entityError('delete', 'Onebox');
            }
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.oneboxListLoaded$.complete();
  }
}